import { v1 as uuidV1 } from 'uuid';
import { config } from '@lingoda/config';
import { getFeatures } from '@lingoda/monolith-feature-flags';
import { getCookieValue, setCookieValue } from '@lingoda/cookie';
import { getStorageValue, memoizePromise } from '@lingoda/utils';
import { getLang } from '@lingoda/env';
import { UNSUPPORTED, getPlatformInfoClientHints } from '@lingoda/user-agent';
import { gaClientId, getTrackingId } from '../ga';
import { loadAnalytics } from './common';
import { getUserAnalyticsInfo } from './userAnalytics';
import type { Event } from './models';

const DISTINCT_ID = 'distinct_id';

const getAuthId = memoizePromise(async () => {
    const Auth = (await import('@aws-amplify/auth')).default;

    return (await Auth.currentCredentials()).identityId;
});

const getAnalyticsData = memoizePromise(async () => ({
    clientId: await gaClientId(),
    trackingId: await getTrackingId(),
}));

const getDistinctId = () => {
    let distinctId = getCookieValue(DISTINCT_ID);
    if (!distinctId) {
        distinctId = uuidV1();
        setCookieValue(DISTINCT_ID, distinctId);
    }

    return distinctId;
};

const getUtmParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const utmParams: Record<string, string> = {};

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

    utmKeys.forEach((key) => {
        const value = searchParams.get(key);
        if (value !== null) {
            utmParams[key] = value;
        }
    });

    return utmParams;
};

const getAppData = () => {
    return {
        sr: `${screen.width}x${screen.height}`,
        vp: `${innerWidth}x${innerHeight}`,
        url: location.href,
        locale: getLang(),
        distinctId: getDistinctId(),
        releaseId: config.releaseId,
    };
};

const getPlatformClientHints = async () => {
    const platformInfoClientHints = await getPlatformInfoClientHints();

    return {
        os_version_client_hints: platformInfoClientHints.osVersion || UNSUPPORTED,
    };
};

const addEvent = async (event: Event) => {
    const Analytics = await loadAnalytics();
    const id = uuidV1();
    const identityId = await getAuthId();
    const platformData = { platform: 'web' };
    const { userId, ...userInfo } = getUserAnalyticsInfo();

    let platformClientHints = {};
    try {
        platformClientHints = await getPlatformClientHints();
    } catch (error) {
        console.error(error);
    }

    const utm = getUtmParams();

    const metadata = {
        ...(event.metadata || {}),
        ...userInfo,
        ...platformData,
        ...platformClientHints,
        ...utm,
    };

    let analyticsData = {};
    try {
        analyticsData = await getAnalyticsData();
    } catch {}

    const record = {
        data: {
            ...event,
            ...getAppData(),
            ...utm,
            ...analyticsData,
            ...platformData,

            features: getFeatures(),
            metadata,
            userId,
            id,
            identityId,
        },
        streamName: 'ClickEventStream',
        partitionKey: id,
    };

    // for debugging purposes
    if (getStorageValue('displayContextLayout')) {
        console.debug('[debug] kinesis event:');
        console.dir(record);
    }

    return Analytics?.record(record, 'AWSKinesis');
};

export default addEvent;
